<script lang="ts">
  import { Image } from '@lib/components';
  import type { BannerBlock } from './types';
  import { onMount } from 'svelte';

  let { block, isFullscreen = false }: { block: BannerBlock; isFullscreen?: boolean } = $props();

  const position = $derived(block.position || 'left');
  const padding = $derived(block.padding || '0.5rem 1rem');
  const sticky = $derived(block.sticky || false);
  const backgroundColor = $derived(block.backgroundColor || 'transparent');
  const backgroundDarkColor = $derived(block.backgroundDarkColor || backgroundColor);
  const fullWidth = $derived(block.layout?.width === 'full');
  const border = $derived(block.border || '');
  const menuItems = $derived(block.menuItems || []);
  const maxWidth = $derived(getMaxWidth());

  function getThemeBg(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-primary-color)',
      'secondary': 'var(--theme-secondary-color)',
      'tertiary': 'var(--theme-tertiary-color)',
    };
    return themeColors[color] || color;
  }

  function getThemeColor(color: string) {
    const themeColors: Record<string, string> = {
      'primary': 'var(--theme-text-on-primary)',
      'secondary': 'var(--theme-text-on-secondary)',
      'tertiary': 'var(--theme-text-on-tertiary)',
    };


    return themeColors[color] || color;
  }

  const resolvedAccentBg = $derived(block.accentColor ? getThemeBg(block.accentColor) : 'var(--theme-secondary-color)');
  const resolvedAccentColor = $derived(block.accentColor ? getThemeColor(block.accentColor) : 'var(--theme-text-on-secondary)');

  // Get appropriate text color based on accent color
  const textOnAccentColor = $derived(getTextColorForAccent(block.accentColor));

  // Active state colors
  const resolvedActiveColor = $derived(block.activeColor ? getThemeColor(block.activeColor) : resolvedAccentColor);
  const resolvedActiveBg = $derived(block.activeBackgroundColor ? getThemeBg(block.activeBackgroundColor) : resolvedAccentBg);

  // Track current URL for active state
  let currentPath = $state('');
  let currentHash = $state('');

  function updateCurrentLocation() {
    if (typeof window !== 'undefined') {
      currentPath = window.location.pathname;
      currentHash = window.location.hash;
    }
  }

  function isActive(href: string): boolean {
    if (!href) return false;

    // Normalize paths by removing trailing slashes for comparison
    const normalizedCurrentPath = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const normalizedHref = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;

    // Handle hash-only links (e.g., #paradigm-shift)
    // Only active if we're on the root path AND hash matches
    if (href.startsWith('#')) {
      return normalizedCurrentPath === '/' && currentHash === href;
    }

    // Handle cross-page hash links (e.g., /#impressum)
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      const targetPath = path || '/';
      const normalizedTargetPath = targetPath.endsWith('/') && targetPath !== '/'
        ? targetPath.slice(0, -1)
        : targetPath;
      return normalizedCurrentPath === normalizedTargetPath && currentHash === `#${hash}`;
    }

    // Handle exact path match (but not if there's a hash - that would be a different state)
    return normalizedCurrentPath === normalizedHref && !currentHash;
  }

  // Reactive statement to check active states whenever path/hash changes
  const activeStates = $derived(menuItems.map(item => ({
    href: item.href,
    active: isActive(item.href)
  })));
  
  function getTextColorForAccent(accentColor?: string) {
    if (!accentColor) return 'var(--theme-text-on-secondary)';

    // Map accent colors to their appropriate text colors
    const textColorMap: Record<string, string> = {
      'primary': 'var(--theme-text-on-primary)',
      'secondary': 'var(--theme-text-on-secondary)',
      'tertiary': 'var(--theme-text-on-tertiary)',
      'accent': 'var(--theme-text-on-accent, white)',
      'success': 'white',
      'warning': 'black',
      'error': 'white'
    };

    return textColorMap[accentColor] || 'white'; // Default to white for custom colors
  }

  function getMaxWidth() {
    if (!block.layout?.width) return isFullscreen ? '800px' : '600px';
    if (block.layout.width === 'full') return '100%';
    return `${block.layout.width}px`;
  }
  
  let mobileMenuOpen = $state(false);
  let navigationMenuRef: HTMLElement;
  let menuReady = $state(false);
  
  // Close mobile menu when window resizes to desktop size or clicking outside
  onMount(() => {
    // Initialize current location
    updateCurrentLocation();

    // Enable transitions after initial render to prevent flash
    requestAnimationFrame(() => {
      menuReady = true;
    });

    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        mobileMenuOpen = false;
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen && navigationMenuRef && !navigationMenuRef.contains(event.target as Node)) {
        mobileMenuOpen = false;
      }
    };

    // Handle hash on page load (for browser back/forward navigation)
    const handleHashOnLoad = () => {
      updateCurrentLocation(); // Update active state
      const hash = window.location.hash;
      if (hash && hash.startsWith('#')) {
        const sectionId = hash.substring(1);
        const element = document.getElementById(sectionId);
        if (element) {
          // Wait longer to ensure all slide animations have completed
          // Slide animations can take up to 1200ms (600ms base + 600ms for multiple blocks)
          setTimeout(() => {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 1500);
        }
      }
    };

    const handleLocationChange = () => {
      updateCurrentLocation();
    };

    // Check hash immediately and also listen for hash changes
    handleHashOnLoad();
    window.addEventListener('hashchange', handleHashOnLoad);
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('hashchange', handleHashOnLoad);
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  });
  
  function isRelativeUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }

  function isExternalUrl(url: string): boolean {
    try {
      return new URL(url, window.location.origin).origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  async function handleNavigation(href: string) {
    // For relative URLs, try to use client-side navigation
    if (isRelativeUrl(href)) {
      try {
        // Dynamically import goto from SvelteKit if available
        const { goto } = await import('$app/navigation').catch(() => ({ goto: null }));
        if (goto) {
          await goto(href);
          // SvelteKit navigation doesn't trigger popstate, so update manually
          setTimeout(() => updateCurrentLocation(), 50);
          return;
        }
      } catch {
        // Fall back to regular navigation if SvelteKit navigation is not available
      }
    }

    // Fall back to regular navigation for external URLs or when goto is not available
    window.location.href = href;
  }

  async function handleClick() {
    if (block.href) {
      const currentPath = window.location.pathname;
      
      // Check if we're clicking a home link and already on the home/root page
      if (block.href === '/' && (currentPath === '/' || currentPath === '')) {
        // Find the actual scrollable element - the landing page component
        const landingPage = document.querySelector('.landing-page-component');
        
        if (landingPage) {
          landingPage.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
        
        return;
      }
      
      // Otherwise navigate normally
      await handleNavigation(block.href);
    }
  }
  
  async function handleMenuItemClick(href: string, target?: string) {
    // Close mobile menu first
    mobileMenuOpen = false;

    // Check if clicking current page - do nothing
    if (isActive(href)) {
      return;
    }

    // Handle external links
    if (target === '_blank') {
      window.open(href, '_blank');
      return;
    }

    // Normalize paths for comparison
    const currentPathNormalized = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const hrefNormalized = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;

    // Special case: clicking same path but we currently have a hash (e.g., /#ccc -> /)
    // This should clear the hash and scroll to top without navigation
    if (currentPathNormalized === hrefNormalized && !href.includes('#') && currentHash) {
      // Clear hash and scroll to top
      window.history.replaceState(null, '', href);
      updateCurrentLocation();

      // Scroll to top
      const landingPage = document.querySelector('.landing-page-component');
      if (landingPage) {
        landingPage.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      return;
    }

    // Handle links with hash (both #anchor and /path#anchor)
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      const targetPath = path || currentPath; // If no path, use current path
      const targetPathNormalized = targetPath.endsWith('/') && targetPath !== '/'
        ? targetPath.slice(0, -1)
        : targetPath;

      // If target path matches current path, just scroll to anchor
      if (currentPathNormalized === targetPathNormalized) {
        const sectionId = hash;
        const element = document.getElementById(sectionId);

        if (element) {
          // Update hash in URL
          window.history.replaceState(null, '', `${currentPath}#${hash}`);
          updateCurrentLocation();

          // Blur any focused elements to remove focus outline
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }

          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          return;
        }
      }
    }

    // Use proper navigation handling for different pages
    await handleNavigation(href);
    // Update will happen via popstate/hashchange events
  }
  
  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
  }
</script>

<div 
  class="banner-block"
  class:fullscreen={isFullscreen}
  class:sticky={sticky}
  class:full-width={fullWidth}
  class:clickable={!!block.href && !menuItems.length}
  style:--padding={padding}
  style:--menu-accent-color={resolvedAccentColor}
  style:--menu-accent-bg={resolvedAccentBg}
  style:--menu-text-color={textOnAccentColor}
  style:--menu-active-color={resolvedActiveColor}
  style:--menu-active-bg={resolvedActiveBg}
  style:--bg-color={backgroundColor}
  style:--bg-dark-color={backgroundDarkColor}
  style:--position={position === 'left' ? 'flex-start' : position === 'right' ? 'flex-end' : 'center'}
  style:--border={border}
  style:--max-width={maxWidth}
  on:click={!menuItems.length && block.href ? () => handleClick() : undefined}
  on:keydown={!menuItems.length && block.href ? (e) => e.key === 'Enter' && handleClick() : undefined}
  role={block.href && !menuItems.length ? 'button' : 'banner'}
  tabindex={block.href && !menuItems.length ? 0 : -1}
>
  <div class="banner-content">
    <div class="banner-inner">
    {#if block.logo}
      <div 
        class="logo-wrapper"
        class:clickable={!!block.href}
        style:--max-width={block.logo.maxWidth}
        style:--max-height={block.logo.maxHeight}
        on:click|stopPropagation={block.href ? () => handleClick() : undefined}
        on:keydown={block.href ? (e) => e.key === 'Enter' && handleClick() : undefined}
        role={block.href ? 'button' : undefined}
        tabindex={block.href ? 0 : -1}
      >
        {#if block.logo.type === 'svg' && block.logo.svg}
          <div class="svg-container">
            {@html block.logo.svg}
          </div>
        {:else if block.logo.type === 'image' && block.logo.src}
          <Image
            src={block.logo.src}
            alt={block.logo.alt || ''}
            backgroundSize="contain"
            sources={block.logo.sources}
            originalSrc={block.logo.originalSrc}
          />
        {:else if block.logo.type === 'component' && block.logo.component}
          <!-- Reserved for animated components -->
          <div class="component-placeholder">
            {block.logo.component}
          </div>
        {/if}
      </div>
    {/if}
    
    {#if menuItems.length > 0}
      <!-- Navigation Menu -->
      <nav class="navigation-menu" class:ready={menuReady} aria-label="Main navigation" bind:this={navigationMenuRef}>
        <!-- Menu Items (shown on desktop, hidden on mobile) -->
        <div class="menu-items" class:open={mobileMenuOpen}>
          {#each menuItems as item}
            <button
              class="menu-item"
              class:active={isActive(item.href)}
              on:click={() => handleMenuItemClick(item.href, item.target)}
            >
              {item.label}
            </button>
          {/each}
        </div>
        
        <!-- Mobile Menu Button (hidden on desktop) -->
        <button 
          class="mobile-menu-button"
          on:click={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <div class="hamburger" class:open={mobileMenuOpen}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </nav>
    {/if}
    </div>
  </div>
</div>

<style lang="postcss">
  .banner-block {
    width: auto;
    max-width: var(--max-width);
    margin: 0 auto;
    background-color: var(--bg-color);
    position: relative;
    z-index: 10;
    padding: var(--padding);
    border: var(--border);
    transition: background-color 200ms ease;
  }
  
  .banner-content {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .banner-inner {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
  
  .banner-block.sticky {
    position: sticky;
    top: 0;
    z-index: 100;
  }
  
  .banner-block.clickable {
    cursor: pointer;
  }
  
  .banner-block.clickable:hover {
    opacity: 0.9;
  }
  
  .banner-block.clickable:focus {
    outline: 2px solid var(--theme-primary-color);
    outline-offset: -2px;
  }

  /* Full-width banners */
  .banner-block.full-width {
    max-width: 100%;
    border-radius: 0;
  }
  
  .logo-wrapper {
    width: var(--max-width);
    height: var(--max-height);
    flex-shrink: 0;
  }
  
  .logo-wrapper.clickable {
    cursor: pointer;
  }
  
  .logo-wrapper.clickable:hover {
    opacity: 0.9;
  }
  
  .logo-wrapper.clickable:focus {
    outline: 2px solid var(--theme-primary-color);
    outline-offset: -2px;
    border-radius: 4px;
  }
  
  .svg-container {
    width: 100%;
    height: 100%;
  }
  
  .svg-container :global(svg) {
    width: 100%;
    height: 100%;
    max-width: var(--max-width);
    max-height: var(--max-height);
  }
  
  .component-placeholder {
    padding: 0.5rem;
    border: 1px dashed #ccc;
    border-radius: 4px;
    color: #666;
    font-size: 0.875rem;
  }
  
  /* Navigation Menu */
  .navigation-menu {
    display: flex;
    align-items: center;
    margin-left: auto;
    position: relative;
  }
  
  .menu-items {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }
  
  .menu-item {
    background: none;
    border: none;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    font-weight: 500;
    font-family: inherit;
    color: var(--theme-text-color);
    cursor: pointer;
    border-radius: 4px;
    text-decoration: none;
  }

  /* Hide menu items until ready to prevent flash of incorrect active states */
  .navigation-menu:not(.ready) .menu-items {
    visibility: hidden;
  }

  /* Apply transitions after component is ready to prevent flash on load */
  .navigation-menu.ready .menu-item {
    transition: background-color 200ms ease-in-out, color 200ms ease-in-out;
  }

  /* Active menu item styles */
  .menu-item.active {
    background-color: var(--menu-active-bg);
    color: var(--menu-active-color);
  }

  /* Only apply hover styles on devices that support hover */
  @media (hover: hover) {
    .menu-item:hover {
      background-color: color-mix(in srgb, var(--menu-accent-bg) 85%, white);
      color: var(--menu-accent-color);
    }

    /* Keep active state visible on hover */
    .menu-item.active:hover {
      background-color: var(--menu-active-bg);
      color: var(--menu-active-color);
      opacity: 0.9;
    }
  }

  .menu-item:focus-visible {
    outline: 2px solid var(--theme-text-color);
    outline-offset: -2px;
  }
  
  /* Mobile Menu Button */
  .mobile-menu-button {
    display: none;
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    margin-left: auto;
  }
  
  .hamburger {
    width: 24px;
    height: 18px;
    position: relative;
    transform: rotate(0deg);
    transition: 0.3s ease-in-out;
  }
  
  .hamburger span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--theme-text-color);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: 0.25s ease-in-out;
  }
  
  .hamburger span:nth-child(1) {
    top: 0px;
  }
  
  .hamburger span:nth-child(2) {
    top: 8px;
  }
  
  .hamburger span:nth-child(3) {
    top: 16px;
  }
  
  .hamburger.open span:nth-child(1) {
    top: 8px;
    transform: rotate(135deg);
  }
  
  .hamburger.open span:nth-child(2) {
    opacity: 0;
    left: -24px;
  }
  
  .hamburger.open span:nth-child(3) {
    top: 8px;
    transform: rotate(-135deg);
  }
  
  /* Mobile Menu Responsive Styles */
  @container (max-width: 768px) {
    .menu-items {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      width: 220px;
      flex-direction: column;
      gap: 0;
      background-color: white;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
      border-radius: 5px;
      overflow: hidden;
      z-index: 1000;
      max-height: 0;
      opacity: 0;
      pointer-events: none;
      transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
      transform: translateY(-8px);
    }

    .menu-items.open {
      max-height: 70vh;
      overflow-y: auto;
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }

    .menu-item {
      width: 100%;
      padding: 0.875rem 1.25rem;
      border-radius: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      text-align: left;
      color: var(--theme-text-color);
      background: transparent;
      font-weight: 500;
      font-size: 0.9375rem;
    }

    .menu-item.active {
      color: var(--theme-primary-color);
      background: rgba(255, 16, 85, 0.06);
    }

    @media (hover: hover) {
      .menu-item:hover {
        background-color: rgba(0, 0, 0, 0.04);
        color: var(--theme-text-color);
      }

      .menu-item.active:hover {
        background: rgba(255, 16, 85, 0.1);
        color: var(--theme-primary-color);
      }
    }

    .menu-item:last-child {
      border-bottom: none;
    }

    .mobile-menu-button {
      display: block;
    }
  }
  
  /* Dark mode support */
  :global([data-theme='dark']) .banner-block {
    background-color: var(--bg-dark-color);
  }
  
  :global([data-theme='dark']) .menu-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @container (max-width: 768px) {
    :global([data-theme='dark']) .menu-items {
      background-color: #1f1f1f;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3);
    }

    :global([data-theme='dark']) .menu-item {
      border-bottom-color: rgba(255, 255, 255, 0.08);
      color: #f3f4f6;
    }

    :global([data-theme='dark']) .menu-item:hover {
      background-color: rgba(255, 255, 255, 0.08);
    }

    :global([data-theme='dark']) .menu-item.active {
      background: rgba(255, 16, 85, 0.15);
    }
  }
  
  @container (max-width: 600px) {
    .banner-block {
      padding: calc(var(--padding) * 0.75);
    }
    
    .logo-wrapper {
      max-width: calc(var(--max-width, 200px) * 0.8);
      max-height: calc(var(--max-height, 60px) * 0.8);
    }
  }
</style>